import React, { useState, useEffect, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Drawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Menu,
  Skeleton,
  Divider,
  Typography,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import DescriptionIcon from '@mui/icons-material/Description';
import PhoneIcon from '@mui/icons-material/Phone';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ReceiptIcon from '@mui/icons-material/Receipt';
import BarChartIcon from '@mui/icons-material/BarChart';
import InventoryIcon from '@mui/icons-material/Inventory';
import LayersIcon from '@mui/icons-material/Layers';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TuneIcon from '@mui/icons-material/Tune';
import PeopleIcon from '@mui/icons-material/People';
import LanguageIcon from '@mui/icons-material/Language';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PersonIcon from '@mui/icons-material/Person';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import SettingsIcon from '@mui/icons-material/Settings';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

import { useAtomValue, useAtom, useSetAtom } from 'jotai';
import { AppAtoms } from '../../lib/store';
import { isProductionEnv } from '../../config/const';
import { logout, handleUpdatePassword } from '../../lib/auth';
import { getProfile, ProfileResponse } from '../../api/profile';
import { PasswordEditModal } from '../profile/PasswordEditModal';
import { PlanUsageCard } from '../plan/PlanUsageCard';
import { poSidebarTokens } from '../../lib/poTheme';
// cmd-po_1168: AI第3列化
import AiChatSideBar from '../AiChatSideBar';

// ─── 定数 ────────────────────────────────────────────────────────
const HEADER_H = 56;
const SIDEBAR_FULL = 248;
// 出自: images/ui/html/s3_tossup_collapsed_pc.html L30-35（base.css非改変=クラスoverride・殿裁定 2026-07-24(a)）
const SIDEBAR_MINI = 64;
// 是正: 旧='var(--color-ai)'(#1e3a5f) → 正='var(--color-ai-dark)'(#0f1d2f) base.css L211-214
const SIDEBAR_BG = 'var(--color-ai-dark)';
const SIDEBAR_TEXT = poSidebarTokens.text;
const SIDEBAR_TEXT_ACTIVE = poSidebarTokens.textActive;
const SIDEBAR_HOVER = poSidebarTokens.hover;
const SIDEBAR_ACTIVE_BG = poSidebarTokens.activeBg;
const SIDEBAR_ACTIVE_BORDER = poSidebarTokens.activeBorder;
const SIDEBAR_GROUP_LABEL = poSidebarTokens.groupLabel;
const DIVIDER_COLOR = poSidebarTokens.divider;

// ─── ページ情報生成（buildTitle置き換え: tenantName連結廃止・crumb+title返却） ─────
function buildPageInfo(
  pathname: string,
  auth: string,
  db: string,
): { crumb: string | null; title: string } {
  const M = auth === 'マスターアカウント';
  const S = auth === 'スタッフアカウント';
  const dbT = db === 'トスアップアカウント';
  const dbE = db === '営業アカウント';
  const P = auth === 'パートナー';
  const billingGroupVisible = M || P;

  let title = '';
  let crumb: string | null = null;

  switch (pathname) {
    case '/':
    case '/dashboard':
      title = 'ホーム'; crumb = '日次業務'; break;
    case '/toss_up':
      title = 'トスアップ'; crumb = '日次業務'; break;
    case '/contract':
      title = 'エントリー'; crumb = '日次業務'; break;
    case '/follow_call':
      title = 'フォローコール'; crumb = '日次業務'; break;
    case '/analytics':
    case '/runking':
    case '/data-analysis':
    case '/product_partner_result':
      title = '分析'; crumb = billingGroupVisible ? '精算' : '日次業務'; break;
    case '/payments':
    case '/payment_admin':
      title = '明細管理'; crumb = '精算'; break;
    case '/payment':
      title = '明細'; crumb = '精算'; break;
    case '/product':
      title = '商材'; crumb = 'マスタ設定'; break;
    case '/service':
      title = 'サービスプラン'; crumb = 'マスタ設定'; break;
    case '/incentive':
      title = 'インセンティブ'; crumb = 'マスタ設定'; break;
    case '/forms':
    case '/entry_form':
    case '/toss_up_form':
    case '/outside_entry_form':
      title = 'フォーム管理'; crumb = 'マスタ設定'; break;
    case '/follow_call_config':
      title = 'フォローコール設定'; crumb = 'マスタ設定'; break;
    case '/outside_entry_site':
      title = !dbT ? '外部ETサイト' : '';
      crumb = 'マスタ設定'; break;
    case '/partner':
      title = 'パートナー'; crumb = 'マスタ設定'; break;
    case '/account':
      title = 'アカウント'; crumb = 'システム'; break;
    case '/column_config':
      title = '画面項目設定'; crumb = 'システム'; break;
    case '/config':
      title = '企業・グループ'; crumb = 'マスタ設定'; break;
    case '/system_config':
      title = 'システム設定'; crumb = 'システム'; break;
    case '/inquiry':
      title = '問い合わせ'; crumb = '日次業務'; break;
    default:
      title = ''; crumb = null; break;
  }
  // 権限外でtitleが空になるケース(outside_entry_site/dbT等)はcrumb不要
  if (!title) crumb = null;
  return { crumb, title };
}


// ─── ナビ定義 ─────────────────────────────────────────────────────
interface NavItemDef {
  path: string;
  label: string;
  icon: ReactNode;
  visible: boolean;
  matchPaths?: string[];
}

interface NavGroupDef {
  groupLabel: string;
  items: NavItemDef[];
}

function buildNavGroups(auth: string, db: string, isSmartPhone: boolean): NavGroupDef[] {
  const M = auth === 'マスターアカウント';
  const S = auth === 'スタッフアカウント';
  const P = auth === 'パートナー';
  const dbT = db === 'トスアップアカウント';
  const dbE = db === '営業アカウント';

  const hasRanking = !dbE && !dbT && !isSmartPhone;
  const hasAI = !isProductionEnv && !isSmartPhone;
  const hasProductResult = (M || S) && !isSmartPhone;
  const analyticsVisible = hasRanking || hasAI || hasProductResult;
  const billingGroupVisible = M || P;

  const outsideEntrySiteLabel = !dbT ? '外部ETサイト' : '';

  const dailyItems: NavItemDef[] = [
    { path: '/dashboard', label: 'ホーム', icon: (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 10.5 12 3l9 7.5"/>
          <path d="M5 9.5V21h14V9.5"/>
        </svg>
      </Box>
    ), visible: true },
    { path: '/toss_up', label: 'トスアップ', icon: <ArrowCircleUpIcon />, visible: true },
    { path: '/contract', label: 'エントリー', icon: <DescriptionIcon />, visible: !dbT },
    { path: '/follow_call', label: 'フォローコール', icon: <PhoneIcon />, visible: !dbT },
    { path: hasRanking ? '/runking' : hasProductResult ? '/product_partner_result' : '/data-analysis', matchPaths: ['/runking', '/product_partner_result', '/data-analysis'], label: '分析', icon: <BarChartIcon />, visible: analyticsVisible && !billingGroupVisible },
    { path: '/inquiry', label: '問い合わせ', icon: <HelpOutlineIcon />, visible: true },
  ];
  const billingItems: NavItemDef[] = [
    // 殿裁定5: '確定済 - 明細' → '明細'（951 M2タブ化で冗長になったため）
    { path: M ? '/payment_admin' : '/payment', matchPaths: ['/payment_admin', '/payment'], label: '明細', icon: <ReceiptIcon />, visible: M || P },
    { path: hasRanking ? '/runking' : hasProductResult ? '/product_partner_result' : '/data-analysis', matchPaths: ['/runking', '/product_partner_result', '/data-analysis'], label: '分析', icon: <BarChartIcon />, visible: analyticsVisible && billingGroupVisible },
  ];
  const masterItems: NavItemDef[] = [
    { path: '/product', label: '商材', icon: <InventoryIcon />, visible: M || S },
    { path: '/service', label: 'サービスプラン', icon: <LayersIcon />, visible: M || S },
    { path: '/incentive', label: 'インセンティブ', icon: <CardGiftcardIcon />, visible: M || S },
    { path: '/entry_form', matchPaths: ['/entry_form', '/toss_up_form', '/outside_entry_form'], label: 'フォーム管理', icon: <AssignmentIcon />, visible: M || S },
    { path: '/follow_call_config', label: 'フォローコール設定', icon: <TuneIcon />, visible: M || S },
    { path: '/partner', label: 'パートナー', icon: <PeopleIcon />, visible: M || S },
    {
      path: '/outside_entry_site',
      label: outsideEntrySiteLabel,
      icon: <LanguageIcon />,
      visible: (M || S) || !dbT,
    },
    { path: '/config', label: '企業・グループ', icon: <ApartmentIcon />, visible: P },
  ];
  const systemItems: NavItemDef[] = [
    { path: '/account', label: 'アカウント', icon: <PersonIcon />, visible: M || S || P },
    { path: '/column_config', label: '画面項目設定', icon: <ViewColumnIcon />, visible: !dbE && !dbT },
    { path: '/system_config', label: 'システム設定', icon: <SettingsIcon />, visible: M },
  ];

  return [
    { groupLabel: '日次業務', items: dailyItems },
    { groupLabel: '精算', items: billingItems },
    { groupLabel: 'マスタ設定', items: masterItems },
    { groupLabel: 'システム', items: systemItems },
  ];
}

// ─── ナビ項目コンポーネント ─────────────────────────────────────────
interface NavItemProps {
  item: NavItemDef;
  mini: boolean;
  active: boolean;
  onNavigate?: () => void;
}

function NavItem({ item, mini, active, onNavigate }: NavItemProps) {
  const [, setPathname] = useAtom(AppAtoms.pathname);
  return (
    <Link
      to={item.path}
      style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
      aria-label={item.label}
      onClick={() => { setPathname(item.path); onNavigate?.(); }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-3)',
          height: 40,
          px: mini ? 0 : 'var(--sp-4)',
          justifyContent: mini ? 'center' : 'flex-start',
          color: active ? SIDEBAR_TEXT_ACTIVE : SIDEBAR_TEXT,
          bgcolor: active ? SIDEBAR_ACTIVE_BG : 'transparent',
          borderLeft: active ? `3px solid ${SIDEBAR_ACTIVE_BORDER}` : '3px solid transparent',
          borderRadius: 'var(--radius-sm)',
          '&:hover': { bgcolor: SIDEBAR_HOVER, color: SIDEBAR_TEXT_ACTIVE },
          fontSize: 'var(--fs-13)',
          fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-regular)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'inline-flex', flexShrink: 0, opacity: active ? 1 : 0.85, '& svg': { fontSize: 18 } }}>
          {item.icon}
        </Box>
        {!mini && (
          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
            {item.label}
          </Box>
        )}
      </Box>
    </Link>
  );
}

// ─── サイドバー本体コンテンツ（アコーディオン廃止→静的グループラベル） ──────────────
interface SidebarContentProps {
  mini: boolean;
  currentPath: string;
  auth: string;
  db: string;
  profileLoaded: boolean;
  onNavigate?: () => void;
}

function SidebarContent({ mini, currentPath, auth, db, profileLoaded, onNavigate }: SidebarContentProps) {
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const groups = buildNavGroups(auth, db, isSmartPhone);

  if (!profileLoaded) {
    return (
      <Box sx={{ p: 1.5, pt: 2, flex: 1 }}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 0.5, borderRadius: 1, bgcolor: DIVIDER_COLOR }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, py: 'var(--sp-3)' }}>
      {groups.map((group) => {
        const visibleItems = group.items.filter(it => it.visible && it.label !== '');
        if (visibleItems.length === 0) return null;
        return (
          <Box key={group.groupLabel} sx={{ mb: 'var(--sp-3)' }}>
            {/* 静的グループラベル: クリック不可・chevron除去（catalog components.css #13 .po-nav .nav-group-label） */}
            {!mini && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  px: 'var(--sp-4)',
                  py: 'var(--sp-2)',
                  color: SIDEBAR_GROUP_LABEL,
                  fontSize: 'var(--fs-10)',
                  fontWeight: 'var(--fw-semibold)',
                  letterSpacing: '0.1em',
                }}
              >
                {group.groupLabel}
              </Box>
            )}
            {mini && <Box sx={{ height: 6 }} />}
            {visibleItems.map(item => (
              <NavItem
                key={item.path + item.label}
                item={item}
                mini={mini}
                active={item.matchPaths ? item.matchPaths.includes(currentPath) : currentPath === item.path}
                onNavigate={onNavigate}
              />
            ))}
          </Box>
        );
      })}
    </Box>
  );
}

// ─── AppShell 本体 ────────────────────────────────────────────────
interface AppShellProps {
  children: ReactNode;
}

function AppShell({ children }: AppShellProps) {
  const loginUser = useAtomValue(AppAtoms.loginUser);
  const profileLoaded = useAtomValue(AppAtoms.profileLoaded);
  const isSmartPhone = useAtomValue(AppAtoms.isSmartPhone);
  const aiChatOpen = useAtomValue(AppAtoms.aiChatOpen);
  const setAiChatOpenAtom = useSetAtom(AppAtoms.aiChatOpen);
  const detailBreadcrumb = useAtomValue(AppAtoms.detailBreadcrumb);
  const headerCreateAction = useAtomValue(AppAtoms.headerCreateAction);
  const headerCreateActions = useAtomValue(AppAtoms.headerCreateActions);
  const location = useLocation();
  const currentPath = location.pathname;
  // cmd-po_1168 rw1: route遷移でAIを閉じる（/contractでopen→/inquiryへ遷移後にai-chat-sidebar DOM無し・1167までの寿命維持）
  useEffect(() => { setAiChatOpenAtom(false); }, [currentPath]);
  // cmd-po_1168: pathname→pageNameマップ（AppShellで一元管理）
  const AI_PAGE_NAME_MAP: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/toss_up': 'TossUp',
    '/follow_call': 'FollowCall',
    '/contract': 'Contract',
    '/runking': 'AccountRunking',
    '/payment_admin': 'Payment',
    '/payment': 'PaymentConfirm',
    '/entry_form': 'EntryForm',
    '/toss_up_form': 'TossUpForm',
    '/outside_entry_form': 'OutsideEntryForm',
    '/product': 'Product',
    '/service': 'Service',
    '/incentive': 'Incentive',
    '/follow_call_config': 'FollowCallConfig',
    '/outside_entry_site': 'OutsideEntrySite',
    '/column_config': 'ScreenColumnConfig',
    '/partner': 'SaleCompany',
    '/account': 'Account',
    '/system_config': 'SystemConfig',
  };
  const aiChatPageName = AI_PAGE_NAME_MAP[currentPath];
  const [miniMode, setMiniMode] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // PC: dropdown anchor / SP: boolean
  const [accountMenuAnchor, setAccountMenuAnchor] = useState<null | HTMLElement>(null);
  const [accountMenuSpOpen, setAccountMenuSpOpen] = useState(false);

  // プロフィール dialog
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [profileData, setProfileData] = useState<ProfileResponse | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // パスワード変更 modal（PasswordEditModal 裁定-2b）
  const [editPassword, setEditPassword] = useState<{
    status: string;
    oldPassword: string;
    newPassword1: string;
    newPassword2: string;
  } | null>(null);

  const auth = loginUser?.authority ?? '';
  const db = loginUser?.db_authority ?? '';
  const tenantName = loginUser?.title ?? '';
  const username = loginUser?.username ?? '';
  const avatarChar = username ? username.charAt(0) : '';
  // po-role-chip 表示用ラベル（'マスターアカウント'→'マスター'等）
  // ③cmd-po_1195: 営業合流バケット(auth==='営業アカウント')のみ db_authority raw で営業family3種を区別表示。
  // master/staff/partner は現行 auth.replace 形を維持（AC差異申告=素formula db||fallback は制約違反のため条件式採用）。
  const roleLabel = (auth === '営業アカウント' && db) ? db : auth.replace('アカウント', '');

  const sidebarWidth = miniMode ? SIDEBAR_MINI : SIDEBAR_FULL;

  // buildPageInfo: tenantName非含有・crumb=navグループ名・title=画面名のみ
  const pageInfo = buildPageInfo(currentPath, auth, db);

  async function loadProfileIfNeeded() {
    if (!profileData) {
      setProfileLoading(true);
      try {
        const data = await getProfile();
        setProfileData(data as ProfileResponse);
      } finally {
        setProfileLoading(false);
      }
    }
  }

  async function openProfileDialog() {
    setProfileDialogOpen(true);
    await loadProfileIfNeeded();
  }

  async function putEditData() {
    if (!editPassword) return;
    setEditPassword({ ...editPassword, status: 'editing' });
    const res = await handleUpdatePassword({
      oldPassword: editPassword.oldPassword,
      newPassword: editPassword.newPassword1,
    });
    setEditPassword({ ...editPassword, status: res });
  }

  function closeAccountMenu() {
    setAccountMenuAnchor(null);
    setAccountMenuSpOpen(false);
  }

  const sidebarSx = {
    width: sidebarWidth,
    height: '100%',
    overflowY: 'auto' as const,
    bgcolor: SIDEBAR_BG,
    color: SIDEBAR_TEXT,
    display: 'flex',
    flexDirection: 'column' as const,
    flexShrink: 0,
  };

  // brand帯: 『MENU』廃止→tenantName表示 (catalog components.css #13 .po-sidebar .brand)
  const brandSection = (
    <Box
      sx={{
        height: HEADER_H,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        px: miniMode ? 0 : 'var(--sp-4)',
        justifyContent: miniMode ? 'center' : 'flex-start',
        flexShrink: 0,
        borderBottom: '1px solid var(--ov-light-08)',
        fontWeight: 'var(--fw-bold)',
        fontSize: 'var(--fs-15)',
        color: 'var(--color-white)',
        letterSpacing: '0.02em',
        overflow: 'hidden',
      }}
    >
      {!miniMode && (
        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tenantName}
        </Box>
      )}
    </Box>
  );

  // SPドロワー用brand帯（miniMode無し固定）
  const brandSectionSp = (
    <Box
      sx={{
        height: HEADER_H,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        px: 'var(--sp-4)',
        flexShrink: 0,
        borderBottom: '1px solid var(--ov-light-08)',
        fontWeight: 'var(--fw-bold)',
        fontSize: 'var(--fs-15)',
        color: 'var(--color-white)',
        letterSpacing: '0.02em',
        overflow: 'hidden',
      }}
    >
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {tenantName}
      </Box>
    </Box>
  );

  const accountMenuItems = (
    <>
      {/* プロフィール (outline person icon・chevron撤去) */}
      <Box
        component="button"
        onClick={() => { closeAccountMenu(); openProfileDialog(); }}
        aria-label="プロフィール"
        sx={{
          display: 'flex', alignItems: 'center',
          width: '100%', px: 2, minHeight: 44, border: 'none', bgcolor: 'transparent',
          cursor: 'pointer', fontSize: 14, '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="span" sx={{ display: 'inline-flex', width: 18, height: 18, flexShrink: 0, color: 'text.secondary' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="8" r="4"/>
              <path d="M4 21c0-4 3.5-6.5 8-6.5s8 2.5 8 6.5"/>
            </svg>
          </Box>
          プロフィール
        </Box>
      </Box>
      {/* パスワード変更（裁定-2b・outline lock icon・chevron撤去） */}
      <Box
        component="button"
        onClick={() => {
          closeAccountMenu();
          setEditPassword({ status: 'before', oldPassword: '', newPassword1: '', newPassword2: '' });
        }}
        aria-label="パスワード変更"
        sx={{
          display: 'flex', alignItems: 'center',
          width: '100%', px: 2, minHeight: 44, border: 'none', bgcolor: 'transparent',
          cursor: 'pointer', fontSize: 14, '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="span" sx={{ display: 'inline-flex', width: 18, height: 18, flexShrink: 0, color: 'text.secondary' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="4" y="10" width="16" height="11" rx="2"/>
              <path d="M8 10V7a4 4 0 0 1 8 0v3"/>
              <circle cx="12" cy="15.5" r="1.4" fill="currentColor"/>
            </svg>
          </Box>
          パスワード変更
        </Box>
      </Box>
      <Divider />
      {/* ログアウト (outline logout icon) */}
      <Box
        component="button"
        onClick={() => { closeAccountMenu(); logout(); }}
        aria-label="ログアウト"
        sx={{
          display: 'flex', alignItems: 'center',
          width: '100%', px: 2, minHeight: 44, border: 'none', bgcolor: 'transparent',
          cursor: 'pointer', fontSize: 14, color: 'error.main', '&:hover': { bgcolor: 'error.light' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box component="span" sx={{ display: 'inline-flex', width: 18, height: 18, flexShrink: 0, color: 'error.main' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <path d="m16 17 5-5-5-5M21 12H9"/>
            </svg>
          </Box>
          ログアウト
        </Box>
      </Box>
    </>
  );

  const accountMenuHeader = (
    <Box
      className="am-user"
      sx={{
        p: 'var(--sp-4)',
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-3)',
      }}
    >
      {/* avatar */}
      <Box
        className="po-avatar"
        sx={{
          width: 40, height: 40, borderRadius: '50%',
          bgcolor: 'primary.main', color: 'var(--color-white)',
          fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {avatarChar}
      </Box>
      {/* am-user-info */}
      <Box className="am-user-info" sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-05)', minWidth: 0, flex: 1 }}>
        {/* am-name-row: 氏名 + role chip */}
        <Box className="am-name-row" sx={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', mb: '2px', flexWrap: 'wrap' }}>
          <Box component="span" className="am-name" sx={{ fontSize: 'var(--fs-14)', fontWeight: 'var(--fw-bold)', color: 'text.primary' }}>
            {username}
          </Box>
          <Box component="span" className="po-role-chip" sx={{
            display: 'inline-flex', alignItems: 'center',
            px: '6px', py: '1px',
            fontSize: 'var(--fs-11)', fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-kohaku)', bgcolor: 'var(--color-kohaku-ov-light-08)',
            borderRadius: 'var(--radius-sm)', lineHeight: 1.6,
          }}>
            {roleLabel}
          </Box>
        </Box>
        {/* am-company */}
        <Box component="span" className="am-company" sx={{ display: 'block', fontSize: 'var(--fs-11)', color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {loginUser?.company ?? ''}
        </Box>
        {/* am-email: profileData 読み込み後に表示 */}
        <Box component="span" className="am-email" sx={{ display: 'block', fontSize: 'var(--fs-11)', color: 'rgb(156, 163, 175)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {profileLoading ? '' : ((profileData?.mail_address as string) ?? '')}
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <Box data-testid="app-shell" sx={{ display: 'flex', bgcolor: 'background.default', ...(isSmartPhone ? { minHeight: '100vh' } : { height: '100vh', overflow: 'hidden' }) }}>
        {/* PC 常設サイドバー */}
        {!isSmartPhone && (
          <Box data-testid="app-sidebar" sx={sidebarSx}>
            {brandSection}
            <SidebarContent
              mini={miniMode}
              currentPath={currentPath}
              auth={auth}
              db={db}
              profileLoaded={profileLoaded}
              onNavigate={() => {}}
            />
            {auth === 'マスターアカウント' && !miniMode && (
              <PlanUsageCard dark />
            )}
          </Box>
        )}

        {/* SP ドロワー（オーバーレイ） */}
        {isSmartPhone && (
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            variant="temporary"
            sx={{
              '& .MuiDrawer-paper': {
                width: 280,
                bgcolor: SIDEBAR_BG,
                color: SIDEBAR_TEXT,
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {brandSectionSp}
            <SidebarContent
              mini={false}
              currentPath={currentPath}
              auth={auth}
              db={db}
              profileLoaded={profileLoaded}
              onNavigate={() => setDrawerOpen(false)}
            />
            {auth === 'マスターアカウント' && <PlanUsageCard dark />}
          </Drawer>
        )}

        {/* メインエリア（min-width:0 が要石: 既存の広いテーブルがレイアウトを突き破らないために必須） */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}>
          {/* グローバルヘッダー */}
          <Box
            component="header"
            sx={{
              height: HEADER_H,
              bgcolor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              px: { xs: 'var(--sp-3)', sm: 'var(--sp-5)' },
              gap: 'var(--sp-3)',
              position: isSmartPhone ? 'fixed' : 'sticky',
              top: 0,
              width: isSmartPhone ? '100%' : undefined,
              zIndex: 1100,
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            {/* ハンバーガー: PC=miniトグル / SP=ドロワートグル（base.css L261-265 .po-icon-btn） */}
            <IconButton
              size="small"
              aria-label="メニュー"
              onClick={() => {
                if (isSmartPhone) {
                  setDrawerOpen(true);
                } else {
                  setMiniMode(prev => !prev);
                }
              }}
              sx={{ color: 'text.secondary', flexShrink: 0, width: 36, height: 36 }}
            >
              <MenuIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* パンくず+短タイトル（detailBreadcrumb機構は完全維持） */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }} className="hdr-tb">
              {detailBreadcrumb ? (
                /* 942基盤: detailBreadcrumb機構 – buildTitle改修と同じ分岐に同居のため巻き込み禁止 */
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {detailBreadcrumb.crumb}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ fontSize: 16, fontWeight: 700, color: 'primary.main', whiteSpace: 'nowrap' }}
                  >
                    {detailBreadcrumb.title}
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* パンくず: navグループ名+『/』・SP時display:none (base.css L274) */}
                  {pageInfo.crumb && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: 'var(--color-sumi-muted)',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                        display: { xs: 'none', sm: 'block' },
                      }}
                    >
                      {pageInfo.crumb} /
                    </Typography>
                  )}
                  {/* 短タイトル: 16px 700 var(--color-ai)・画面名のみ (base.css L276) */}
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: 'primary.main',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {pageInfo.title}
                  </Typography>
                </>
              )}
            </Box>

            {/* ヘッダー右端: 作成ボタン注入枠 + ベル + アバター */}

            {/* 作成ボタン注入: 複数ボタン（M5: 2本独立）or 1本 */}
            {headerCreateActions ? (
              <Box sx={{ display: 'inline-flex', gap: 1, flexShrink: 0 }}>
                {headerCreateActions.map((action, idx) => (
                  <Box
                    key={action.label}
                    component="button"
                    data-testid={action.testId}
                    onClick={action.handler}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 0.5,
                      height: 36, px: 2, borderRadius: '6px',
                      ...(idx === headerCreateActions.length - 1
                        ? { bgcolor: 'primary.main', color: 'var(--color-white)', border: 'none' }
                        : { bgcolor: 'background.paper', color: 'primary.main', border: '1.5px solid', borderColor: 'primary.main' }),
                      cursor: 'pointer', fontSize: 13, fontWeight: 600,
                      '&:hover': idx === headerCreateActions.length - 1 ? { bgcolor: 'primary.dark' } : { bgcolor: 'var(--state-hover)' },
                    }}
                  >
                    {/* catalog #01 create-btn: ＋svgアイコン付与（枠責務）*/}
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    <span className="create-label">{action.label}</span>
                  </Box>
                ))}
              </Box>
            ) : headerCreateAction ? (
              <Box
                component="button"
                data-testid={headerCreateAction.testId}
                onClick={headerCreateAction.handler}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  height: 36, px: 2, borderRadius: '6px',
                  bgcolor: 'primary.main', color: 'var(--color-white)',
                  border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                {/* catalog #01 create-btn: ＋svgアイコン付与（枠責務）*/}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" style={{ flexShrink: 0 }}>
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="create-label">{headerCreateAction.label}</span>
              </Box>
            ) : null}

            {/* 通知ベル: 描画のみ・機能未実装（モック準拠・新機能発明禁止） */}
            <IconButton
              size="small"
              aria-label="通知"
              sx={{ color: 'text.secondary', width: 36, height: 36, flexShrink: 0 }}
              disabled
            >
              <NotificationsNoneIcon sx={{ fontSize: 20 }} />
            </IconButton>

            {/* アバター（アカウントメニュートリガー）aria-haspopup="menu" / aria-expanded付き */}
            <Box
              component="button"
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                if (isSmartPhone) {
                  setAccountMenuSpOpen(true);
                } else {
                  setAccountMenuAnchor(e.currentTarget);
                }
                // メニュー展開時にプロフィール（メール）を先行ロード
                loadProfileIfNeeded();
              }}
              aria-label="アカウントメニュー"
              aria-haspopup="menu"
              aria-expanded={Boolean(accountMenuAnchor) || accountMenuSpOpen ? 'true' : 'false'}
              sx={{
                width: 32, height: 32, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'var(--color-white)',
                fontSize: 'var(--fs-12)', fontWeight: 'var(--fw-bold)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: 'none', cursor: 'pointer', flexShrink: 0,
                '&:hover': { bgcolor: 'primary.light' },
                '&:focus': {
                  outline: '3px solid var(--state-focus-ring)',
                  outlineOffset: 2,
                },
              }}
            >
              {avatarChar}
            </Box>

            {/* PC アカウントメニュー */}
            <Menu
              anchorEl={accountMenuAnchor}
              open={Boolean(accountMenuAnchor)}
              onClose={() => setAccountMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: {
                  sx: {
                    width: 300,
                    mt: '-4px', // top:52px = 56px header – 4px（モック準拠）
                    borderRadius: 'var(--radius-md, 8px)',
                    boxShadow: 'var(--shadow-elevated)',
                    overflow: 'hidden',
                    zIndex: 120,
                  },
                },
              }}
            >
              {accountMenuHeader}
              <Box>{accountMenuItems}</Box>
            </Menu>
          </Box>

          {/* コンテンツエリア */}
          <Box
            component="main"
            data-testid="app-main"
            sx={{
              flex: 1,
              minHeight: 0,
              mt: isSmartPhone ? 'var(--header-h)' : 0,
              padding: isSmartPhone ? 'var(--sp-4)' : 'var(--sp-2)',
              ...(isSmartPhone ? {} : { display: 'flex', flexDirection: 'column', overflowY: 'auto' }),
            }}
          >
            {children}
          </Box>
        </Box>
        {/* cmd-po_1168: AI（PC=root第3列100vh / SP=fixed全面overlay）AppShell一元描画 */}
        {aiChatOpen && (
          <AiChatSideBar
            open={aiChatOpen}
            setOpen={setAiChatOpenAtom}
            pageName={aiChatPageName}
          />
        )}
      </Box>

      {/* SP 全画面アカウントシート */}
      {isSmartPhone && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 130,
            bgcolor: 'background.paper',
            display: accountMenuSpOpen ? 'flex' : 'none',
            flexDirection: 'column',
          }}
          role="dialog"
          aria-modal="true"
          aria-label="アカウント"
        >
          <Box
            sx={{
              display: 'flex', alignItems: 'center',
              height: HEADER_H, px: 2, flexShrink: 0,
              borderBottom: '1px solid', borderColor: 'divider',
            }}
          >
            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>アカウント</Typography>
            <IconButton
              aria-label="閉じる"
              size="small"
              onClick={() => setAccountMenuSpOpen(false)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box
              sx={{
                width: 48, height: 48, borderRadius: '50%',
                bgcolor: 'primary.main', color: 'var(--color-white)',
                fontSize: 16, fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {avatarChar}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{username}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {loginUser?.company ?? ''}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {accountMenuItems}
          </Box>
        </Box>
      )}

      {/* プロフィール Dialog (F2: cs-profile-row 4行 + DialogActions閉じる) */}
      {/* cmd-po_1247: 背景クリック/ESCで閉じない（onClose撤去+disableEscapeKeyDown）。閉じるは footer「閉じる」のみ */}
      <Dialog
        open={profileDialogOpen}
        disableEscapeKeyDown
        fullWidth
        PaperProps={{ sx: { borderRadius: 'var(--radius-lg)', width: 560, maxWidth: 'calc(100vw - 32px)' } }}
      >
        <DialogTitle className="sip-head" sx={{ display: 'flex', alignItems: 'center' }}>
          プロフィール
          {/* cmd-po_1247: 自前✕(CloseIcon)を撤去。閉じるは footer「閉じる」へ集約。CloseIcon import は :942 SPアカウントシートで使用継続のため残置 */}
        </DialogTitle>
        <DialogContent className="sip-body" sx={{ p: 0 }}>
          {profileLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              {/* 氏名 */}
              <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>氏名</Box>
                <Box component="span" data-testid="profile-name" sx={{ fontSize: 'var(--fs-13)', color: 'text.primary', ml: 2 }}>{username}</Box>
              </Box>
              {/* 権限 */}
              <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>権限</Box>
                <Box component="span" className="po-role-chip" sx={{
                  display: 'inline-flex', alignItems: 'center',
                  px: '6px', py: '1px',
                  fontSize: 'var(--fs-11)', fontWeight: 'var(--fw-semibold)',
                  color: 'var(--color-kohaku)', bgcolor: 'var(--color-kohaku-ov-light-08)',
                  borderRadius: 'var(--radius-sm)', lineHeight: 1.6,
                }}>{roleLabel}</Box>
              </Box>
              {/* メールアドレス */}
              <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>メールアドレス</Box>
                <Box component="span" data-testid="profile-email" sx={{ fontSize: 'var(--fs-13)', color: 'text.primary', ml: 2, wordBreak: 'break-all' }}>{(profileData?.mail_address as string) ?? '—'}</Box>
              </Box>
              {/* 所属 */}
              <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>所属</Box>
                <Box component="span" data-testid="profile-company" sx={{ fontSize: 'var(--fs-13)', color: 'text.primary', ml: 2 }}>{loginUser?.company ?? '—'}</Box>
              </Box>
              {/* 電話番号（旧PROFILE_COLUMNS gate=4ロール全＝無条件・cmd-po_1131復元） */}
              <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>電話番号</Box>
                <Box component="span" data-testid="profile-phone-number" sx={{ fontSize: 'var(--fs-13)', color: 'text.primary', ml: 2 }}>{(profileData?.phone_number as string) ?? '—'}</Box>
              </Box>
              {/* 外部ドメインキー（パートナー・営業アカウントのみ） */}
              {['パートナー', '営業アカウント'].includes(auth) && (
                <Box className="cs-profile-row" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box component="span" className="po-label" sx={{ color: 'text.secondary', fontSize: 'var(--fs-13)', flexShrink: 0 }}>外部ドメインキー</Box>
                  <Box component="span" data-testid="profile-outside-entry-site-key" sx={{ fontSize: 'var(--fs-13)', color: 'text.primary', ml: 2, wordBreak: 'break-all' }}>{(profileData?.outside_entry_site_key as string) ?? '—'}</Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions className="sip-foot" sx={{ px: 3, py: 2 }}>
          <Button
            variant="outlined"
            className="po-btn po-btn-secondary"
            onClick={() => setProfileDialogOpen(false)}
            sx={{ fontSize: 'var(--fs-13)', color: 'text.secondary', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover', borderColor: 'text.disabled' } }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* cmd-po_1249（殿裁定 2026-08-27）: 操作サポートは常に表示。ログイン済みアプリシェル内の全ページで無条件描画。
          データ分析側の本番封印（AiChatSideBar showDataTab / PageTabs AI分析 / /data-analysis route guard / hasAI）は維持。
          出自: モック .po-chat-fab / position:fixed / right:24px / bottom:84px / z:80 */}
        <Box
          component="button"
          onClick={() => setAiChatOpenAtom(prev => !prev)}
          aria-label="AIサポート"
          className="po-chat-fab"
          sx={{
            position: 'fixed',
            right: 'var(--sp-5, 24px)',
            bottom: 84,
            zIndex: 80,
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: 'var(--color-kohaku)',
            color: 'var(--color-white)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 'var(--shadow-elevated)',
            '&:hover': { filter: 'brightness(1.1)' },
          }}
        >
          <SmartToyIcon sx={{ fontSize: 24 }} />
        </Box>

      {/* パスワード変更 Modal（裁定-2b: 既存 PasswordEditModal 流用・submit経路不変） */}
      {editPassword && (
        <PasswordEditModal
          onModal={editPassword !== null}
          swCloseModal={() => setEditPassword(null)}
          editPassword={editPassword}
          setEditPassword={setEditPassword}
          putEditData={putEditData}
        />
      )}
    </>
  );
}

export default AppShell;
